import { IComposableChartsWidget, ChartFrequency } from 'wisestep-api';

import { generalTimePeriodList } from '../../../../analyticsPage/helper/timePeriodList';
import {
  SubmissionsBarChartPostgre,
  SubmissionsMetricChartPostgre,
  SubmissionsTablePostgre,
} from '../../../BaseCharts/submissions/new';
import { ChartScope } from '../../../types';
import { Widget, getChartFreqForTimePeriod } from '../../helper';

const getChartWidgetByTimeAndScope = (
  timePeriodId: string,
  scope: ChartScope,
  frequency: ChartFrequency = getChartFreqForTimePeriod[timePeriodId]
): Partial<Record<Widget, IComposableChartsWidget>> => {
  const defaultQuery = {
    category: 'submission',
    filters: [],
    type: 'SUBMISSION_COUNT',
    frequency,
    range: timePeriodId,
    scope,
  };
  const freqInRegularCase = frequency.charAt(0).toUpperCase() + frequency.slice(1).toLowerCase();

  const widgets = {
    [Widget.METRIC_TOTAL]: {
      name: 'Total Submissions',
      filters: {},
      type: `metric_submissions_${timePeriodId}_${scope}_postgre`,
      category: 'submissions',
      component: (
        <SubmissionsMetricChartPostgre
          query={{ ...defaultQuery, type: 'SUBMISSION_COUNT' }}
          title="Total Submissions"
          widgetName={Widget.METRIC_TOTAL}
        />
      ),
    },
    [Widget.TABLE_BY_USERS]: {
      name: 'Submissions By Users',
      filters: {},
      type: `table_users_submissions_${timePeriodId}_${scope}_postgre`,
      category: 'submissions',
      component: (
        <SubmissionsTablePostgre
          query={{ ...defaultQuery, type: 'RECRUITER_NAME' }}
          title="Submissions By Users"
          widgetName={Widget.TABLE_BY_USERS}
          widgetType={`table_users_submissions_${timePeriodId}_${scope}_postgre`}
        />
      ),
    },
    [Widget.TABLE_TO_CLIENTS]: {
      name: 'Submissions By Clients',
      filters: {},
      type: `table_clients_submissions_${timePeriodId}_${scope}_postgre`,
      category: 'submissions',
      component: (
        <SubmissionsTablePostgre
          query={{ ...defaultQuery, type: 'CLIENTS' }}
          title="Submissions By Clients"
          widgetName={Widget.TABLE_TO_CLIENTS}
          widgetType={`table_clients_submissions_${timePeriodId}_${scope}_postgre`}
        />
      ),
    },
    [Widget.TABLE_BY_JOBTYPE]: {
      name: 'Submissions By Job Type',
      filters: {},
      type: `table_submissions_by_jobType_${timePeriodId}_${scope}_postgre`,
      category: 'submissions',
      component: (
        <SubmissionsTablePostgre
          query={{ ...defaultQuery, type: 'JOB_TYPE' }}
          title="Submissions By Job Type"
          widgetName={Widget.TABLE_BY_JOBTYPE}
          widgetType={`table_submissions_by_jobType_${timePeriodId}_${scope}_postgre`}
        />
      ),
    },
    [Widget.TABLE_BY_SOURCE]: {
      name: 'Submissions By Source',
      filters: {},
      type: `table_submissions_by_source_${timePeriodId}_${scope}_postgre`,
      category: 'submissions',
      component: (
        <SubmissionsTablePostgre
          query={{ ...defaultQuery, type: 'SOURCE' }}
          title="Submissions By Source"
          widgetName={Widget.TABLE_BY_SOURCE}
          widgetType={`table_submissions_by_source_${timePeriodId}_${scope}_postgre`}
        />
      ),
    },
    [Widget.TABLE_BY_DOWNLOAD_SOURCE]: {
      name: 'Submissions By Download Source',
      filters: {},
      type: `table_submissions_by_download_source_${timePeriodId}_${scope}_postgre`,
      category: 'submissions',
      component: (
        <SubmissionsTablePostgre
          query={{ ...defaultQuery, type: 'DOWNLOAD_SOURCE' }}
          title="Submissions By Download Source"
          widgetName={Widget.TABLE_BY_DOWNLOAD_SOURCE}
          widgetType={`table_submissions_by_download_source_${timePeriodId}_${scope}_postgre`}
        />
      ),
    },
    [Widget.TABLE_TO_AM]: {
      name: 'Submissions By Account Managers',
      filters: {},
      type: `table_am_submissions_${timePeriodId}_${scope}_postgre`,
      category: 'submissions',
      component: (
        <SubmissionsTablePostgre
          query={{ ...defaultQuery, type: 'ACCOUNT_MANAGER' }}
          title="Submissions By Account Managers"
          widgetName={Widget.TABLE_TO_AM}
          widgetType={`table_am_submissions_${timePeriodId}_${scope}_postgre`}
        />
      ),
    },
    [Widget.TABLE_CLIENTWISE]: {
      name: 'Submissions By Client and Client Contact',
      filters: {},
      type: `table_client_wise_${timePeriodId}_${scope}_postgre`,
      category: 'submissions',
      component: (
        <SubmissionsTablePostgre
          query={{ ...defaultQuery, type: 'CLIENTS_AND_CONTACTS' }}
          title="Submissions By Client and Client Contact"
          widgetName={Widget.TABLE_CLIENTWISE}
          widgetType={`table_client_wise_${timePeriodId}_${scope}_postgre`}
        />
      ),
    },
    [Widget.TABLE_BY_CLIENT_SPOC]: {
      name: 'Submissions By Client Contacts',
      filters: {},
      type: `table_submissions_client_and_client_spoc_${timePeriodId}_${scope}_postgre`,
      category: 'submissions',
      component: (
        <SubmissionsTablePostgre
          query={{ ...defaultQuery, type: 'CLIENT_SPOC' }}
          title="Submissions By Client Contacts"
          widgetName={Widget.TABLE_BY_CLIENT_SPOC}
          widgetType={`table_submissions_client_and_client_spoc_${timePeriodId}_${scope}_postgre`}
        />
      ),
    },
    [Widget.TABLE_USERS_WITH_TEAMS]: {
      name: 'Submissions By Users With Teams',
      filters: {},
      type: `table_submissions_by_rec_${timePeriodId}_${scope}_postgre`,
      category: 'submissions',
      component: (
        <SubmissionsTablePostgre
          query={{ ...defaultQuery, type: 'RECRUITER_TEAM' }}
          title="Submissions By Users With Teams"
          widgetName={Widget.TABLE_USERS_WITH_TEAMS}
          widgetType={`table_submissions_by_rec_${timePeriodId}_${scope}_postgre`}
        />
      ),
    },
    [Widget.TABLE_JOB_TITLE]: {
      name: 'Submissions By Job Title',
      filters: {},
      type: `table_submissions_jobs_worked_${timePeriodId}_${scope}_postgre`,
      category: 'submissions',
      component: (
        <SubmissionsTablePostgre
          query={{ ...defaultQuery, type: 'JOB_TITLE' }}
          title="Submissions By Job Title"
          widgetName={Widget.TABLE_JOB_TITLE}
          widgetType={`table_submissions_jobs_worked_${timePeriodId}_${scope}_postgre`}
        />
      ),
    },
    [Widget.TABLE_RECRUITERWISE]: {
      name: 'Recruiter Submissions By Job Type',
      filters: {},
      type: `table_submissions_job_type_by_rec_${timePeriodId}_${scope}_postgre`,
      category: 'submissions',
      component: (
        <SubmissionsTablePostgre
          query={{ ...defaultQuery, type: 'RECRUITER_TEAM_JOB_TYPE' }}
          title="Recruiter Submissions By Job Type"
          widgetName={Widget.TABLE_RECRUITERWISE}
          widgetType={`table_submissions_job_type_by_rec_${timePeriodId}_${scope}_postgre`}
        />
      ),
    },
    [Widget.STACKEDBAR_CLIENTS]: {
      name: `Submissions By Clients - ${freqInRegularCase}`,
      filters: {},
      type: `stackedbarchart_submissions_${timePeriodId}_${scope}_${frequency}_postgre`,
      category: 'submissions',
      component: (
        <SubmissionsBarChartPostgre
          query={{ ...defaultQuery, type: 'STACKED_CLIENTS' }}
          title={`Submissions By Clients - ${freqInRegularCase}`}
          widgetName={Widget.STACKEDBAR_CLIENTS}
        />
      ),
    },
    [Widget.BAR_TOTAL]: {
      name: `Submissions - ${freqInRegularCase}`,
      filters: {},
      type: `barchart_submissions_${timePeriodId}_${scope}_${frequency}_postgre`,
      category: 'submissions',
      component: (
        <SubmissionsBarChartPostgre
          query={{ ...defaultQuery, type: 'SUBMISSION_COUNT_BAR' }}
          title={`Submissions - ${freqInRegularCase}`}
          widgetName={Widget.BAR_TOTAL}
        />
      ),
    },
    [Widget.TABLE_SPLIT_BY_STAGES]: {
      name: 'Submissions By Stages',
      filters: {},
      type: `table_submisions_by_stages_${timePeriodId}_${scope}_postgre`,
      category: 'submissions',
      component: (
        <SubmissionsTablePostgre
          query={{ ...defaultQuery, type: 'APPLICATION_STAGE' }}
          title="Submissions By Stages"
          widgetName={Widget.TABLE_SPLIT_BY_STAGES}
          widgetType={`table_submisions_by_stages_${timePeriodId}_${scope}_postgre`}
        />
      ),
    },
    [Widget.TABLE_BY_AM]: {
      name: 'Submissions by Account Manager',
      filters: {},
      type: `table_submissions_by_am_${timePeriodId}_${scope}_week_postgre`,
      category: 'submissions',
      component: (
        <SubmissionsTablePostgre
          query={{ ...defaultQuery, type: 'ACCOUNT_MANAGER_JOB_TYPE' }}
          title="Submissions by Account Manager"
          widgetName={Widget.TABLE_BY_AM}
          widgetType={`table_submissions_by_am_${timePeriodId}_${scope}_week_postgre`}
        />
      ),
    },
    [Widget.METRIC_JOINING_DATE]: {
      name: 'Total Joined candidates',
      filters: {},
      type: `metric_submissions_joining_date_${timePeriodId}_${scope}_postgre`,
      category: 'submissions',
      component: (
        <SubmissionsMetricChartPostgre
          query={{
            ...defaultQuery,
            type: 'JOINING_DATE',
            filterDate: 'joining_date',
            filters: [
              {
                filterName: 'STAGE_STATUS',
                values: [JSON.stringify({ stage: 'Joining', selectedStatuses: ['Joined'] })],
              },
            ],
          }}
          title="Total Joined candidates"
          widgetName={Widget.METRIC_JOINING_DATE}
        />
      ),
    },
    [Widget.TABLE_JOINING_DATE]: {
      name: 'Joined candidates by Joining Date',
      filters: {},
      type: `table_submissions_joining_date_${timePeriodId}_${scope}_postgre`,
      category: 'submissions',
      component: (
        <SubmissionsTablePostgre
          query={{
            ...defaultQuery,
            type: 'JOINING_DATE',
            filterDate: 'joining_date',
            filters: [
              {
                filterName: 'STAGE_STATUS',
                values: [JSON.stringify({ stage: 'Joining', selectedStatuses: ['Joined'] })],
              },
            ],
          }}
          title="Joined candidates by Joining Date"
          widgetName={Widget.TABLE_JOINING_DATE}
          widgetType={`table_submissions_joining_date_${timePeriodId}_${scope}_postgre`}
        />
      ),
    },
    [Widget.TABLE_RECRUITER_WISE_STAGES]: {
      name: 'Recruiter wise Stages',
      filters: {},
      type: `table_submissions_recruiter_stages_${timePeriodId}_${scope}_postgre`,
      category: 'submissions',
      component: (
        <SubmissionsTablePostgre
          query={{
            ...defaultQuery,
            type: 'RECRUITER_WISE_STAGES',
          }}
          title="Recruiter wise Stages"
          widgetName={Widget.TABLE_RECRUITER_WISE_STAGES}
          widgetType={`table_submissions_recruiter_stages_${timePeriodId}_${scope}_postgre`}
        />
      ),
    },
    [Widget.TABLE_TIME_TO_SERVICE]: {
      name: 'Time Taken to Submit Candidates',
      filters: {},
      type: `table_submissions_service_${timePeriodId}_${scope}_postgre`,
      category: 'submissions',
      component: (
        <SubmissionsTablePostgre
          query={{
            ...defaultQuery,
            type: 'TABLE_TIME_TO_SERVICE',
          }}
          title="Time Taken to Submit Candidates"
          widgetName={Widget.TABLE_TIME_TO_SERVICE}
          widgetType={`table_submissions_service_${timePeriodId}_${scope}_postgre`}
        />
      ),
    },
    [Widget.TABLE_CANDIDATE_WISE_TIME_TO_SERVICE]: {
      name: 'Candidate Wise Time to Service',
      filters: {},
      type: `table_submissions_candidate_service_${timePeriodId}_${scope}_postgre`,
      category: 'submissions',
      component: (
        <SubmissionsTablePostgre
          query={{
            ...defaultQuery,
            type: 'TABLE_CANDIDATE_WISE_TIME_TO_SERVICE',
          }}
          title="Candidate Wise Time to Service"
          widgetName={Widget.TABLE_CANDIDATE_WISE_TIME_TO_SERVICE}
          widgetType={`table_submissions_candidate_service_${timePeriodId}_${scope}_postgre`}
        />
      ),
    },
    [Widget.TABLE_CANDIDATE_WISE_TIME_TO_SERVICE_MEDIAN]: {
      name: 'Stages Wise Time to Service',
      filters: {},
      type: `table_submissions_candidate_service_median_${timePeriodId}_${scope}_postgre`,
      category: 'submissions',
      component: (
        <SubmissionsTablePostgre
          query={{
            ...defaultQuery,
            type: 'TABLE_CANDIDATE_WISE_TIME_TO_SERVICE_MEDIAN',
          }}
          title="Stages Wise Time to Service"
          widgetName={Widget.TABLE_CANDIDATE_WISE_TIME_TO_SERVICE_MEDIAN}
          widgetType={`table_submissions_candidate_service_median_${timePeriodId}_${scope}_postgre`}
        />
      ),
    },
    [Widget.TABLE_SUBMISSION_ASSIGN_TIME_TO_SERVICE]: {
      name: 'User Queue status',
      filters: {},
      type: `table_submissions_assign_service_${timePeriodId}_${scope}_postgre`,
      category: 'submissions',
      component: (
        <SubmissionsTablePostgre
          query={{
            ...defaultQuery,
            type: 'TABLE_SUBMISSION_ASSIGN_TIME_TO_SERVICE',
          }}
          title="User Queue status"
          widgetName={Widget.TABLE_SUBMISSION_ASSIGN_TIME_TO_SERVICE}
          widgetType={`table_submissions_assign_service_${timePeriodId}_${scope}_postgre`}
        />
      ),
    },
    [Widget.METRIC_INTERVIEW_ANALYTICS_1]: {
      name: 'Total Interview 1',
      filters: {},
      type: `metric_interview_1_${timePeriodId}_${scope}_postgre`,
      category: 'submissions',
      component: (
        <SubmissionsMetricChartPostgre
          query={{ ...defaultQuery, type: 'METRIC_INTERVIEW_ANALYTICS_1' }}
          title="Total Interview 1"
          widgetName={Widget.METRIC_INTERVIEW_ANALYTICS_1}
        />
      ),
    },
    [Widget.METRIC_INTERVIEW_ANALYTICS_2]: {
      name: 'Total Interview 2',
      filters: {},
      type: `metric_interview_2_${timePeriodId}_${scope}_postgre`,
      category: 'submissions',
      component: (
        <SubmissionsMetricChartPostgre
          query={{ ...defaultQuery, type: 'METRIC_INTERVIEW_ANALYTICS_2' }}
          title="Total Interview 2"
          widgetName={Widget.METRIC_INTERVIEW_ANALYTICS_2}
        />
      ),
    },
    [Widget.METRIC_INTERVIEW_ANALYTICS_3]: {
      name: 'Total Interview 3',
      filters: {},
      type: `metric_interview_3_${timePeriodId}_${scope}_postgre`,
      category: 'submissions',
      component: (
        <SubmissionsMetricChartPostgre
          query={{ ...defaultQuery, type: 'METRIC_INTERVIEW_ANALYTICS_3' }}
          title="Total Interview 3"
          widgetName={Widget.METRIC_INTERVIEW_ANALYTICS_3}
        />
      ),
    },
    [Widget.METRIC_INTERVIEW_ANALYTICS_4]: {
      name: 'Total Interview 4',
      filters: {},
      type: `metric_interview_4_${timePeriodId}_${scope}_postgre`,
      category: 'submissions',
      component: (
        <SubmissionsMetricChartPostgre
          query={{ ...defaultQuery, type: 'METRIC_INTERVIEW_ANALYTICS_4' }}
          title="Total Interview 4"
          widgetName={Widget.METRIC_INTERVIEW_ANALYTICS_4}
        />
      ),
    },
    [Widget.METRIC_INTERVIEW_ANALYTICS_1_TO_4]: {
      name: 'Total Interview',
      filters: {},
      type: `metric_interview_1_to_4_${timePeriodId}_${scope}_postgre`,
      category: 'submissions',
      component: (
        <SubmissionsMetricChartPostgre
          query={{ ...defaultQuery, type: 'METRIC_INTERVIEW_ANALYTICS_1_TO_4' }}
          title="Total Interview"
          widgetName={Widget.METRIC_INTERVIEW_ANALYTICS_1_TO_4}
        />
      ),
    },
    [Widget.METRIC_INTERVIEW_ANALYTICS_ADVANCED_2_TO_4]: {
      name: 'Advance Interview',
      filters: {},
      type: `metric_interview_advance_${timePeriodId}_${scope}_postgre`,
      category: 'submissions',
      component: (
        <SubmissionsMetricChartPostgre
          query={{ ...defaultQuery, type: 'METRIC_INTERVIEW_ANALYTICS_ADVANCED_2_TO_4' }}
          title="Advance Interview"
          widgetName={Widget.METRIC_INTERVIEW_ANALYTICS_ADVANCED_2_TO_4}
        />
      ),
    },
    [Widget.TABLE_INTERVIEW_ANALYTICS_1]: {
      name: 'Interview 1 - Candidate Wise',
      filters: {},
      type: `table_interview_1_${timePeriodId}_${scope}_postgre`,
      category: 'submissions',
      component: (
        <SubmissionsTablePostgre
          query={{ ...defaultQuery, type: 'TABLE_INTERVIEW_ANALYTICS_1' }}
          title="Interview 1 - Candidate Wise"
          widgetName={Widget.TABLE_INTERVIEW_ANALYTICS_1}
        />
      ),
    },
    [Widget.TABLE_INTERVIEW_ANALYTICS_2]: {
      name: 'Interview 2 - Candidate Wise',
      filters: {},
      type: `table_interview_2_${timePeriodId}_${scope}_postgre`,
      category: 'submissions',
      component: (
        <SubmissionsTablePostgre
          query={{ ...defaultQuery, type: 'TABLE_INTERVIEW_ANALYTICS_2' }}
          title="Interview 2 - Candidate Wise"
          widgetName={Widget.TABLE_INTERVIEW_ANALYTICS_2}
        />
      ),
    },
    [Widget.TABLE_INTERVIEW_ANALYTICS_3]: {
      name: 'Interview 3 - Candidate Wise',
      filters: {},
      type: `table_interview_3_${timePeriodId}_${scope}_postgre`,
      category: 'submissions',
      component: (
        <SubmissionsTablePostgre
          query={{ ...defaultQuery, type: 'TABLE_INTERVIEW_ANALYTICS_3' }}
          title="Interview 3 - Candidate Wise"
          widgetName={Widget.TABLE_INTERVIEW_ANALYTICS_3}
        />
      ),
    },
    [Widget.TABLE_INTERVIEW_ANALYTICS_4]: {
      name: 'Interview 4 - Candidate Wise',
      filters: {},
      type: `table_interview_4_${timePeriodId}_${scope}_postgre`,
      category: 'submissions',
      component: (
        <SubmissionsTablePostgre
          query={{ ...defaultQuery, type: 'TABLE_INTERVIEW_ANALYTICS_4' }}
          title="Interview 4 - Candidate Wise"
          widgetName={Widget.TABLE_INTERVIEW_ANALYTICS_4}
        />
      ),
    },
    [Widget.TABLE_INTERVIEW_ANALYTICS_ADVANCED_2_TO_4]: {
      name: 'Advance Interview - Candidate Wise',
      filters: {},
      type: `table_interview_advance_${timePeriodId}_${scope}_postgre`,
      category: 'submissions',
      component: (
        <SubmissionsTablePostgre
          query={{ ...defaultQuery, type: 'TABLE_INTERVIEW_ANALYTICS_ADVANCED_2_TO_4' }}
          title="Advance Interview - Candidate Wise"
          widgetName={Widget.TABLE_INTERVIEW_ANALYTICS_ADVANCED_2_TO_4}
        />
      ),
    },
  };

  return widgets;
};

export const submissionsChartsKeyPostgre = (
  timePeriodId: string,
  scope: ChartScope
): IComposableChartsWidget[] => {
  const defaultWidgets = Object.values(getChartWidgetByTimeAndScope(timePeriodId, scope));
  const dailyCharts = getChartWidgetByTimeAndScope(timePeriodId, scope, ChartFrequency.DAILY);
  const weeklyCharts = getChartWidgetByTimeAndScope(timePeriodId, scope, ChartFrequency.WEEKLY);

  const additionalWidgets = {
    ['30D']: [weeklyCharts[Widget.BAR_TOTAL], weeklyCharts[Widget.STACKEDBAR_CLIENTS]],
    ['MONTH_TILL_DATE']: [weeklyCharts[Widget.BAR_TOTAL], weeklyCharts[Widget.STACKEDBAR_CLIENTS]],
    ['CURRENT_QUARTER']: [
      dailyCharts[Widget.BAR_TOTAL],
      weeklyCharts[Widget.BAR_TOTAL],
      weeklyCharts[Widget.STACKEDBAR_CLIENTS],
      dailyCharts[Widget.STACKEDBAR_CLIENTS],
    ],
  };

  return [...defaultWidgets, ...(additionalWidgets[timePeriodId] ?? [])];
};

export const postgreSubmissionsDashboardKeysObjectList = generalTimePeriodList.flatMap(
  (timePeriod) => [
    ...submissionsChartsKeyPostgre(timePeriod.id, ChartScope.WORKSPACE),
    ...submissionsChartsKeyPostgre(timePeriod.id, ChartScope.TEAMS),
    ...submissionsChartsKeyPostgre(timePeriod.id, ChartScope.LOGGED_IN_USER),
  ]
);
